import * as RadixAccordion from '@radix-ui/react-accordion';
import type { ComponentPropsWithoutRef } from 'react';

// ─── Root ─────────────────────────────────────────────────────────────────────

export type AccordionRootProps = ComponentPropsWithoutRef<typeof RadixAccordion.Root>;

/**
 * Raiz do Accordion. Aceita `type="single"` (um item aberto por vez) ou
 * `type="multiple"` (vários itens abertos simultaneamente).
 *
 * @example
 * ```tsx
 * <Accordion type="single" collapsible>
 *   <AccordionItem value="item-1">
 *     <AccordionTrigger>Título</AccordionTrigger>
 *     <AccordionContent>Conteúdo</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 * ```
 */
export function Accordion(props: AccordionRootProps) {
  return <RadixAccordion.Root {...props} />;
}

// ─── Item ─────────────────────────────────────────────────────────────────────

export type AccordionItemProps = ComponentPropsWithoutRef<typeof RadixAccordion.Item>;

/**
 * Item individual do Accordion. Requer `value` único dentro do Root.
 */
export function AccordionItem({ className = '', ...props }: AccordionItemProps) {
  return <RadixAccordion.Item className={className} {...props} />;
}

// ─── Trigger ──────────────────────────────────────────────────────────────────

export type AccordionTriggerProps = ComponentPropsWithoutRef<typeof RadixAccordion.Trigger>;

/**
 * Botão que expande/recolhe o item do Accordion.
 */
export function AccordionTrigger({ className = '', children, ...props }: AccordionTriggerProps) {
  return (
    <RadixAccordion.Header asChild>
      <RadixAccordion.Trigger
        className={`group flex w-full items-center text-left transition-colors ${className}`}
        {...props}
      >
        {children}
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
}

// ─── Content ──────────────────────────────────────────────────────────────────

export type AccordionContentProps = ComponentPropsWithoutRef<typeof RadixAccordion.Content>;

/**
 * Conteúdo colapsável do item do Accordion.
 * Inclui animação de abertura/fechamento via `data-state`.
 */
export function AccordionContent({ className = '', ...props }: AccordionContentProps) {
  return (
    <RadixAccordion.Content
      className={`overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up ${className}`}
      {...props}
    />
  );
}
